import { Channel } from 'lib-shared-types-channels';
import { ReactNode } from 'react';

// Type-only so this does not create a runtime cycle with the dropdown.
import type { CopilotAction } from 'components/copilotDropdown';
import {
  EditorCopilotDropdownFeedback,
  EditorCopilotDropdownImproveReply,
  EditorCopilotDropdownMatchToStyleGuide,
  EditorCopilotDropdownSpellingAndGrammar,
  EditorCopilotDropdownTranslate,
  EditorCopilotDropdownTranslateToConversationLanguage,
} from 'components/editorCopilotDropdown/components';
import { CustomAiDraftContent } from 'components/editorCopilotDropdown/components/customAiDraft';
import { EditorCopilotDropdownExpandText } from 'components/editorCopilotDropdown/components/expandText/copilotDropdownExpandText';
import { EditorCopilotDropdownQuickAiDraft } from 'components/editorCopilotDropdown/components/quickAiDraft';

import {
  type CopilotActionId,
  type CopilotDisabledImprovement,
} from './copilotActions.types';

/** What an editor supplies so its action panels can be constructed. */
export type CopilotActionParams = {
  channel?: Channel;
  csid?: string;
  /** The conversation's language, for quick translate. */
  languageCode?: string;
  /** Whether the channel has a writing style guide configured. */
  hasStyleGuideForChannel?: boolean;
  /** Whether a default AI agent is set, for quick draft. */
  hasDefaultDraftAgent?: boolean;
};

/** The editor's params plus the parts of its context a panel needs. */
export type CopilotPanelArgs = CopilotActionParams & {
  disabledImprovements?: CopilotDisabledImprovement[];
};

/** The dropdown panel each action shows, so the builder can look it up by id. */
export const copilotActionPanels: Record<
  CopilotActionId,
  (args: CopilotPanelArgs) => ReactNode
> = {
  improveReply: ({ csid, disabledImprovements }) => (
    <EditorCopilotDropdownImproveReply
      csid={csid}
      disabledImprovements={disabledImprovements}
    />
  ),

  improveReplyMatchStyleGuide: ({ channel, csid }) => (
    <EditorCopilotDropdownMatchToStyleGuide channel={channel} csid={csid} />
  ),

  translateToConversationLanguage: ({ languageCode, csid }) => (
    <EditorCopilotDropdownTranslateToConversationLanguage
      languageCode={languageCode ?? ''}
      csid={csid ?? ''}
    />
  ),

  translate: ({ csid }) => <EditorCopilotDropdownTranslate csid={csid} />,

  checkSpellingAndGrammar: ({ csid }) => (
    <EditorCopilotDropdownSpellingAndGrammar csid={csid} />
  ),

  expandText: ({ channel, csid }) => (
    <EditorCopilotDropdownExpandText channel={channel} csid={csid} />
  ),

  quickAiDraft: ({ csid }) => <EditorCopilotDropdownQuickAiDraft csid={csid} />,

  customAiDraft: ({ csid }) => (
    <CustomAiDraftContent dixaConversationId={csid ?? ''} />
  ),
};

/**
 * Feedback has no registry entry because it does not act on the editor text, so
 * it is not pinnable and has no execution. The menu always ends with it.
 */
export const copilotSendFeedbackAction = (): CopilotAction => ({
  title: 'Send feedback',
  icon: 'paper-plane',
  hasDividerBefore: true,
  content: <EditorCopilotDropdownFeedback />,
});
